import { Main as Container } from 'Components/Main';
import { useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { deleteGapAnalysisVinculo } from '../../../services/gap-analysis';
import { getComplianceProgramDetails } from '../../../services/complice-program';
import { Dialog, Select, TextField, PageHeader, Button } from '../../../Components/ui';
import { typeLabelKeys, statusControl, maturityLevelKeys, translateDomain, sortNumeric, sortMaturityLevels } from '../../../utils/labels_utils';
import { useSelector } from 'react-redux';
import { useAuthTenant } from '../../../store/auth';
import { PageHelper } from '../../../Components/PageHelper';
import { useTranslation } from 'react-i18next';
import { SnackbarAlert } from 'Components/common/SnackbarAlert';
import styled from 'styled-components';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--color-brand)';
const BLUE_DEEP = 'var(--color-brand-strong)';
const CYAN = 'var(--vo-capri)';
const BG = 'var(--vo-mist)';
const BORDER = 'var(--vo-line)';
const DIVIDER = 'var(--vo-cloud)';

const BtnGroup = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-shrink: 0;
`;

/* ── Legend card ── */
const InfoCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  border-left: 4px solid ${CYAN};
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;
const LegendTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 8px;
`;
const LegendItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;
const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;
const LegendSquare = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;
const LegendItemName = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${INK};
`;
const LegendItemDesc = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
`;

/* ── Progress bar card ── */
const ProgressCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
`;
const SectionLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 14px;
`;
const ProgressBarWrap = styled.div`
  display: flex;
  gap: 1px;
  border-radius: 8px;
  overflow: hidden;
  height: 28px;
  margin-bottom: 12px;
`;
const ProgressSegment = styled.div`
  flex: ${({ $flex }) => $flex};
  background: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Maven Pro', sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: ${({ $light }) => $light ? MUTED : 'white'};
  min-width: 0;
  overflow: hidden;
`;
const ProgressLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
`;
const ProgressLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
`;
const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

/* ── Filters card ── */
const FiltersCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
`;
const FilterGrid4 = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 12px;
`;
const FilterGrid3 = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
`;
const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;
const FilterLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${MUTED};
`;
const FilterBtns = styled.div`
  display: flex;
  gap: 10px;
`;
const ClearBtn = styled.button`
  height: 40px;
  background: white;
  color: ${MUTED};
  border: 1.5px solid ${BORDER};
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: ${BG}; }
`;

/* ── Controls table ── */
const CtrlCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
`;
const CtrlCardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${DIVIDER};
`;
const CtrlCardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
  margin: 0;
`;
const CtrlTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;
const CtrlTh = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  padding: 11px 16px;
  text-align: ${({ $center }) => $center ? 'center' : 'left'};
  border-bottom: 1px solid ${DIVIDER};
  background: ${BG};
`;
const ParentTr = styled.tr`
  border-bottom: 1px solid var(--vo-mist);
  background: var(--vo-mist);
  &:hover { background: var(--vo-mist); }
`;
const ExpandBtn = styled.button`
  width: 22px;
  height: 22px;
  border: 1px solid ${BORDER};
  background: white;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${MUTED};
  padding: 0;
`;
const CtrlTd = styled.td`
  padding: 11px 16px;
`;
const CodeTd = styled(CtrlTd)`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: var(--vo-steel);
`;
const NameTd = styled(CtrlTd)`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${INK};
  font-weight: 500;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const DomainChip = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${BLUE_DEEP};
  background: var(--color-brand-8);
  padding: 2px 9px;
  border-radius: 10px;
`;
const WeightTd = styled(CtrlTd)`
  text-align: center;
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: var(--vo-ink-2);
`;
const LinkedChip = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 9px;
  border-radius: 10px;
  background: ${({ $linked }) => $linked ? 'var(--vo-success-10)' : BG};
  color: ${({ $linked }) => $linked ? 'var(--vo-success)' : MUTED};
`;
const ActionTd = styled(CtrlTd)`
  text-align: center;
`;

/* ── Entity sub-rows ── */
const SubHeaderTr = styled.tr`
  background: var(--vo-capri-50);
  border-bottom: 1px solid var(--vo-capri-100);
`;
const SubTh = styled.td`
  padding: 7px 16px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  text-align: ${({ $center }) => $center ? 'center' : 'left'};
`;
const EntityTr = styled.tr`
  border-bottom: 1px solid var(--vo-capri-50);
  background: var(--vo-capri-50);
  &:hover { background: var(--vo-capri-50); }
`;
const EntityDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--vo-steel);
  flex-shrink: 0;
`;
const TypeChip = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${BLUE_DEEP};
  background: var(--color-brand-8);
  padding: 2px 8px;
  border-radius: 10px;
`;
const StatusDotEnt = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

/* ── Not found ── */
const NotFoundCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 32px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
`;
const NotFoundTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${INK};
  margin: 0;
`;
const NotFoundDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${MUTED};
  margin: 0;
`;

const EMPTY_FILTERS = { peso: '', dominio: '', vinculo: '', entidadeTipo: '', entidadeNome: '', status: '', maturityLevel: '' };

export const ComplianceProgramShow = () => {
  const { t } = useTranslation(['company', 'gap-analysis', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const selectedTenant = useSelector(useAuthTenant);

  const [itemToDelete, setItemToDelete] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [openIds, setOpenIds] = useState([]);
  const [tableSearch, setTableSearch] = useState('');

  useEffect(() => {
    const linkResult = location.state?.linkResult;
    if (!linkResult) return;

    const severityByCode = {
      GA_LINK_CREATED: 'success',
      GA_LINK_ALREADY_EXISTS: 'warning',
      GA_LINK_PARTIAL: 'warning',
      GA_LINK_ERROR: 'error',
    };
    const messageKeyByCode = {
      GA_LINK_CREATED: 'gap-analysis:controlsLink.messages.created',
      GA_LINK_ALREADY_EXISTS: 'gap-analysis:controlsLink.messages.alreadyLinked',
      GA_LINK_PARTIAL: 'gap-analysis:controlsLink.messages.partial',
      GA_LINK_ERROR: 'gap-analysis:controlsLink.messages.error',
    };

    setSnackbar({
      open: true,
      message: t(messageKeyByCode[linkResult.code] || messageKeyByCode.GA_LINK_ERROR),
      severity: severityByCode[linkResult.code] || 'error',
    });
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const { isLoading, error, refetch, data: gapAnalysis } = useQuery('getComplianceProgramDetails', async () => {
    try {
      const data = await getComplianceProgramDetails(selectedTenant.slug);
      return data.status === 200 ? data.data : null;
    } catch (err) {
      if (err?.response?.status === 404) return { hasComplianceProgram: false };
      throw err;
    }
  });

  const tourSteps = [
    { target: 'body', content: t('company:complianceProgram.show.tour.step0'), placement: 'center', disableBeacon: true },
    { target: '[data-tour="page-title"]', content: t('company:complianceProgram.show.tour.step1'), disableScrolling: true },
    { target: '[data-tour="vincular-button"]', content: t('company:complianceProgram.show.tour.step2'), disableScrolling: true },
    { target: '[data-tour="status-bar"]', content: t('company:complianceProgram.show.tour.step3'), disableScrolling: true },
    { target: '[data-tour="status-info"]', content: t('company:complianceProgram.show.tour.step4'), disableScrolling: true },
    { target: '[data-tour="filters"]', content: t('company:complianceProgram.show.tour.step5') },
    { target: '[data-tour="controls-table"]', content: t('company:complianceProgram.show.tour.step6') },
    { target: 'body', content: t('company:complianceProgram.show.tour.step7'), placement: 'center' },
    { target: 'body', content: t('company:complianceProgram.show.tour.step8'), placement: 'center' },
  ];

  if (isLoading) return <p>{t('company:complianceProgram.loading')}</p>;
  if (error) return <p>{t('company:complianceProgram.loadingError')}</p>;

  // The program is a per-company singleton whose stored name is a fixed
  // system string (saved in Portuguese by vo-api-ga), so the page shows the
  // localized feature name rather than the raw stored value.
  const programTitle = t('common:sidebar.privacyProgram');

  if (gapAnalysis?.hasComplianceProgram === false) {
    return (
      <Container>
        <PageHeader
          title={programTitle}
          segments={[
            { label: t('common:breadcrumbs.manage') },
            { label: programTitle },
          ]}
        />
        <NotFoundCard data-testid="program-empty-state">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><line x1="12" y1="9" x2="12" y2="15" /><line x1="9" y1="12" x2="15" y2="12" />
          </svg>
          <NotFoundTitle>{t('company:complianceProgram.emptyState.title')}</NotFoundTitle>
          <NotFoundDesc>{t('company:complianceProgram.emptyState.description')}</NotFoundDesc>
          <Button
            variant="primary"
            type="button"
            onClick={() => navigate('/compliance-program/add')}
            sx={{ height: '40px', padding: '0 18px', marginTop: '4px' }}
          >
            {t('company:complianceProgram.emptyState.cta')}
          </Button>
        </NotFoundCard>
      </Container>
    );
  }

  const id = gapAnalysis.gapAnalysis?._id;

  const formattedStatuses = Object.keys(statusControl).map(status => ({
    label: t(statusControl[status].labelKey),
    count: gapAnalysis.statusCounts?.[status] || 0,
    color: statusControl[status].color,
  }));
  const totalCount = formattedStatuses.reduce((s, st) => s + st.count, 0);

  const uniqueValues = key => [...new Set(gapAnalysis.controls.map(c => c[key]).filter(Boolean))].sort();
  const uniqueWeights = sortNumeric(uniqueValues('controlWeighting'));

  const getUniqueEntities = () => {
    const entityMap = new Map();
    const entityTypes = new Set();
    const entityStatuses = new Set();
    const maturityLevelsSet = new Set();
    gapAnalysis.controls.forEach(control => {
      control.entities.forEach(entity => {
        const key = `${entity.entity_id}-${entity.entity_type}`;
        if (!entityMap.has(key)) entityMap.set(key, { entity_id: entity.entity_id, entity_name: entity.entity_name });
        entityTypes.add(entity.entity_type);
        entityStatuses.add(entity.status);
        maturityLevelsSet.add(entity.maturity_level);
      });
    });
    return {
      entities: Array.from(entityMap.values()),
      entityTypes: Array.from(entityTypes),
      entityStatuses: Array.from(entityStatuses),
      maturityLevels: sortMaturityLevels(Array.from(maturityLevelsSet)),
    };
  };

  const { entities, entityTypes, entityStatuses, maturityLevels } = getUniqueEntities();

  // '' means genuinely no selection; 'all' is the real, explicit "All" menu
  // choice — both mean "don't filter on this field," distinct only in how
  // the Select renders them (muted placeholder vs. full-strength value).
  const isFilterActive = (value) => value && value !== 'all';

  const filteredControls = gapAnalysis.controls
    .map(control => {
      const filteredEntities = (isFilterActive(filters.entidadeNome) || isFilterActive(filters.entidadeTipo) || isFilterActive(filters.maturityLevel) || isFilterActive(filters.status))
        ? control.entities.filter(entity =>
            (isFilterActive(filters.entidadeTipo) ? entity.entity_type === filters.entidadeTipo : true) &&
            (isFilterActive(filters.entidadeNome) ? entity.entity_id === filters.entidadeNome : true) &&
            (isFilterActive(filters.maturityLevel) ? entity.maturity_level === filters.maturityLevel : true) &&
            (isFilterActive(filters.status) ? entity.status === filters.status : true)
          )
        : control.entities;
      return { ...control, entities: filteredEntities };
    })
    .filter(control =>
      (control.entities.length > 0 || !(isFilterActive(filters.entidadeNome) || isFilterActive(filters.entidadeTipo) || isFilterActive(filters.maturityLevel) || isFilterActive(filters.status))) &&
      (isFilterActive(filters.peso) ? parseInt(control.controlWeighting, 10) === parseInt(filters.peso, 10) : true) &&
      (isFilterActive(filters.dominio) ? control.scfDomain === filters.dominio : true) &&
      (isFilterActive(filters.vinculo) ? control.vinculado.toString() === filters.vinculo : true)
    );

  const searchLower = tableSearch.toLowerCase();
  const displayControls = tableSearch
    ? filteredControls.filter(c =>
        (c.scfId ?? '').toLowerCase().includes(searchLower) ||
        (c.scfControl ?? '').toLowerCase().includes(searchLower)
      )
    : filteredControls;

  const toggleRow = rowId =>
    setOpenIds(prev => prev.includes(rowId) ? prev.filter(x => x !== rowId) : [...prev, rowId]);

  const onDelete = item => { setItemToDelete(item); setOpenDeleteModal(true); };
  const onCloseDeleteModal = () => { setOpenDeleteModal(false); setItemToDelete(null); };
  const onConfirmDeleteModal = async () => {
    try {
      await deleteGapAnalysisVinculo(itemToDelete._id, id);
      setSnackbar({ open: true, message: t('company:complianceProgram.show.delete.successMessage'), severity: 'success' });
      refetch();
      onCloseDeleteModal();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message ?? t('company:complianceProgram.show.delete.errorMessage'), severity: 'error' });
      onCloseDeleteModal();
    }
  };

  return (
    <>
      <PageHelper steps={tourSteps} tourKey="compliance-program-show-tour" autoStart={true} showButton={true} />
      <Container>
        {/* ── Page header ── */}
        <PageHeader
          data-tour="page-title"
          title={programTitle}
          segments={[
            { label: t('common:breadcrumbs.manage') },
            { label: programTitle },
          ]}
          actions={
            <BtnGroup data-tour="vincular-button">
              <Button
                variant="primary"
                type="button"
                onClick={() => navigate(`/compliance-program/show/${id}/vincular`)}
                startIcon={
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                }
                sx={{ height: '40px', padding: '0 18px' }}
              >
                {t('company:complianceProgram.show.linkControls')}
              </Button>
              <Button
                variant="outline"
                type="button"
                onClick={() => navigate('/reports/compliance-program')}
                startIcon={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
                  </svg>
                }
                sx={{ height: '40px', padding: '0 18px' }}
              >
                {t('gap-analysis:show.dashboardButton')}
              </Button>
            </BtnGroup>
          }
        />

        {/* ── Legend card ── */}
        <div data-tour="status-info">
          <InfoCard>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={CYAN} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <div>
              <LegendTitle>{t('company:complianceProgram.show.legendTitle')}</LegendTitle>
              <LegendItems>
                {Object.entries(statusControl).map(([key, { labelKey, color, descriptionKey }]) => (
                  <LegendItem key={key}>
                    <LegendSquare $color={color} />
                    <LegendItemName>{t(labelKey)}</LegendItemName>
                    <LegendItemDesc>— {t(descriptionKey)}</LegendItemDesc>
                  </LegendItem>
                ))}
              </LegendItems>
            </div>
          </InfoCard>
        </div>

        {/* ── Progress bar card ── */}
        <div data-tour="status-bar">
          <ProgressCard>
            <SectionLabel>{t('company:complianceProgram.show.progressSection')}</SectionLabel>
            {totalCount > 0 && (
              <ProgressBarWrap>
                {formattedStatuses.filter(s => s.count > 0).map(({ label, count, color }) => {
                  const pct = Math.round((count / totalCount) * 100);
                  const isLight = color === 'var(--vo-line)' || color === 'var(--vo-cloud)';
                  return (
                    <ProgressSegment key={label} $flex={count} $color={color} $light={isLight}>
                      {pct >= 6 ? `${pct}%` : ''}
                    </ProgressSegment>
                  );
                })}
              </ProgressBarWrap>
            )}
            <ProgressLegend>
              {formattedStatuses.map(({ label, count, color }) => (
                <ProgressLegendItem key={label}>
                  <LegendDot $color={color} />
                  {label} ({count})
                </ProgressLegendItem>
              ))}
            </ProgressLegend>
          </ProgressCard>
        </div>

        {/* ── Filters card ── */}
        <FiltersCard data-tour="filters">
          <SectionLabel>{t('company:complianceProgram.show.filterTitle')}</SectionLabel>
          <FilterGrid4>
            <FilterField>
              <FilterLabel>{t('company:complianceProgram.show.filters.domain')}</FilterLabel>
              <Select
                name="dominio"
                value={filters.dominio}
                onChange={value => setFilters(f => ({ ...f, dominio: value }))}
                options={[
                  { value: '', label: t('company:complianceProgram.show.filters.selectDomain') },
                  { value: 'all', label: t('company:complianceProgram.show.filters.all') },
                  ...uniqueValues('scfDomain').map(v => ({ value: v, label: translateDomain(t, v) })),
                ]}
              />
            </FilterField>
            <FilterField>
              <FilterLabel>{t('company:complianceProgram.show.filters.bond')}</FilterLabel>
              <Select
                name="vinculo"
                value={filters.vinculo}
                onChange={value => setFilters(f => ({ ...f, vinculo: value }))}
                options={[
                  { value: '', label: t('company:complianceProgram.show.filters.selectBond') },
                  { value: 'all', label: t('company:complianceProgram.show.filters.all') },
                  { value: 'true', label: t('company:complianceProgram.show.filters.yes') },
                  { value: 'false', label: t('company:complianceProgram.show.filters.no') },
                ]}
              />
            </FilterField>
            <FilterField>
              <FilterLabel>{t('company:complianceProgram.show.filters.status')}</FilterLabel>
              <Select
                name="status"
                value={filters.status}
                onChange={value => setFilters(f => ({ ...f, status: value }))}
                options={[
                  { value: '', label: t('company:complianceProgram.show.filters.selectStatus') },
                  { value: 'all', label: t('company:complianceProgram.show.filters.all') },
                  ...entityStatuses.map(s => ({ value: s, label: statusControl[s] ? t(statusControl[s].labelKey) : s })),
                ]}
              />
            </FilterField>
            <FilterField>
              <FilterLabel>{t('company:complianceProgram.show.filters.entityName')}</FilterLabel>
              <Select
                name="entidadeNome"
                value={filters.entidadeNome}
                onChange={value => setFilters(f => ({ ...f, entidadeNome: value }))}
                options={[
                  { value: '', label: t('company:complianceProgram.show.filters.selectEntityName') },
                  { value: 'all', label: t('company:complianceProgram.show.filters.allFeminine') },
                  ...entities.map(entity => ({ value: entity.entity_id, label: entity.entity_name })),
                ]}
              />
            </FilterField>
          </FilterGrid4>
          <FilterGrid3>
            <FilterField>
              <FilterLabel>{t('company:complianceProgram.show.filters.entityType')}</FilterLabel>
              <Select
                name="entidadeTipo"
                value={filters.entidadeTipo}
                onChange={value => setFilters(f => ({ ...f, entidadeTipo: value }))}
                options={[
                  { value: '', label: t('company:complianceProgram.show.filters.selectEntityType') },
                  { value: 'all', label: t('company:complianceProgram.show.filters.all') },
                  ...entityTypes.map(type => ({ value: type, label: typeLabelKeys[type] ? t(typeLabelKeys[type]) : type })),
                ]}
              />
            </FilterField>
            <FilterField>
              <FilterLabel>{t('company:complianceProgram.show.filters.maturity')}</FilterLabel>
              <Select
                name="maturityLevel"
                value={filters.maturityLevel}
                onChange={value => setFilters(f => ({ ...f, maturityLevel: value }))}
                options={[
                  { value: '', label: t('company:complianceProgram.show.filters.selectMaturity') },
                  { value: 'all', label: t('company:complianceProgram.show.filters.all') },
                  ...maturityLevels.map(level => ({ value: level, label: maturityLevelKeys[level] ? t(maturityLevelKeys[level]) : level })),
                ]}
              />
            </FilterField>
            <FilterField>
              <FilterLabel>{t('company:complianceProgram.show.filters.weight')}</FilterLabel>
              <Select
                name="peso"
                value={filters.peso}
                onChange={value => setFilters(f => ({ ...f, peso: value }))}
                options={[
                  { value: '', label: t('company:complianceProgram.show.filters.selectWeight') },
                  { value: 'all', label: t('company:complianceProgram.show.filters.all') },
                  ...uniqueWeights.map(v => ({ value: v, label: v })),
                ]}
              />
            </FilterField>
          </FilterGrid3>
          <FilterBtns>
            <ClearBtn type="button" onClick={() => setFilters(EMPTY_FILTERS)}>
              {t('company:complianceProgram.show.clearButton')}
            </ClearBtn>
            <Button
              variant="primary"
              type="button"
              startIcon={
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              }
              sx={{ height: '40px', padding: '0 20px' }}
            >
              {t('company:complianceProgram.show.filterButton')}
            </Button>
          </FilterBtns>
        </FiltersCard>

        {/* ── Controls table ── */}
        <CtrlCard data-tour="controls-table">
          <CtrlCardHead>
            <CtrlCardTitle>
              {t('company:complianceProgram.show.controls')} ({displayControls.length})
            </CtrlCardTitle>
            <TextField
              placeholder={t('gap-analysis:show.searchPlaceholder')}
              value={tableSearch}
              onChange={e => setTableSearch(e.target.value)}
              sx={{ width: 220 }}
            />
          </CtrlCardHead>
          <CtrlTable>
            <thead>
              <tr>
                <CtrlTh style={{ width: 28 }} />
                <CtrlTh>{t('gap-analysis:table.controlCode')}</CtrlTh>
                <CtrlTh>{t('gap-analysis:table.control')}</CtrlTh>
                <CtrlTh>{t('gap-analysis:table.domain')}</CtrlTh>
                <CtrlTh $center>{t('gap-analysis:table.controlWeight')}</CtrlTh>
                <CtrlTh $center>{t('gap-analysis:table.link')}</CtrlTh>
                <CtrlTh $center>{t('gap-analysis:table.actions')}</CtrlTh>
              </tr>
            </thead>
            <tbody>
              {displayControls.map(control => {
                const rowId = control._id ?? control.scfId;
                const isOpen = openIds.includes(rowId);
                return (
                  <React.Fragment key={rowId}>
                    <ParentTr>
                      <CtrlTd style={{ textAlign: 'center', width: 28 }}>
                        <ExpandBtn type="button" onClick={() => toggleRow(rowId)}>
                          <svg
                            width="11" height="11" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2.5"
                            style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </ExpandBtn>
                      </CtrlTd>
                      <CodeTd>{control.scfId}</CodeTd>
                      <NameTd title={control.scfControl}>{control.scfControl}</NameTd>
                      <CtrlTd>
                        <DomainChip>{translateDomain(t, control.scfDomain)}</DomainChip>
                      </CtrlTd>
                      <WeightTd>{control.controlWeighting}</WeightTd>
                      <ActionTd>
                        <LinkedChip $linked={control.vinculado}>
                          {control.vinculado ? t('company:complianceProgram.show.filters.yes') : t('company:complianceProgram.show.filters.no')}
                        </LinkedChip>
                      </ActionTd>
                      <ActionTd>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <Button iconOnly variant="icon" title={t('gap-analysis:controlLink.title')} onClick={() => navigate(`/compliance-program/${id}/vincular/${control._id}`)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                            </svg>
                          </Button>
                        </div>
                      </ActionTd>
                    </ParentTr>

                    {isOpen && (
                      <>
                        <SubHeaderTr>
                          <SubTh />
                          <SubTh colSpan={2}>{t('company:complianceProgram.show.filters.entityName')}</SubTh>
                          <SubTh>{t('common:labels.type')}</SubTh>
                          <SubTh $center>{t('common:labels.status')}</SubTh>
                          <SubTh $center>{t('gap-analysis:table.maturityLevel')}</SubTh>
                          <SubTh $center>{t('gap-analysis:table.actions')}</SubTh>
                        </SubHeaderTr>
                        {control.entities.map(entity => (
                          <EntityTr key={entity._id ?? entity.entity_id}>
                            <CtrlTd />
                            <CtrlTd colSpan={2}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 8 }}>
                                <EntityDot />
                                <span style={{ fontFamily: "'Lato', sans-serif", fontSize: 13, color: 'var(--vo-ink-2)' }}>
                                  {entity.entity_name}
                                </span>
                              </div>
                            </CtrlTd>
                            <CtrlTd>
                              <TypeChip>
                                {typeLabelKeys[entity.entity_type] ? t(typeLabelKeys[entity.entity_type]) : entity.entity_type}
                              </TypeChip>
                            </CtrlTd>
                            <ActionTd>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'center' }}>
                                <StatusDotEnt $color={statusControl[entity.status]?.color ?? MUTED} />
                                <span style={{ fontFamily: "'Maven Pro', sans-serif", fontSize: 12, color: MUTED }}>
                                  {statusControl[entity.status] ? t(statusControl[entity.status].labelKey) : entity.status}
                                </span>
                              </div>
                            </ActionTd>
                            <ActionTd style={{ fontFamily: "'Maven Pro', sans-serif", fontSize: 12, color: MUTED }}>
                              {maturityLevelKeys[entity.maturity_level] ? t(maturityLevelKeys[entity.maturity_level]) : entity.maturity_level}
                            </ActionTd>
                            <ActionTd>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                                <Button
                                  iconOnly
                                  variant="icon"
                                  title={t('common:tooltips.view')}
                                  onClick={() => navigate(`/compliance-program/control/show/${entity._id}?gapanalysis_id=${id}`)}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                                  </svg>
                                </Button>
                                <Button iconOnly variant="icon" danger title={t('company:complianceProgram.show.delete.confirm', { name: '' })} onClick={() => onDelete(entity)}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                  </svg>
                                </Button>
                              </div>
                            </ActionTd>
                          </EntityTr>
                        ))}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </CtrlTable>
        </CtrlCard>

        <Dialog
          open={openDeleteModal}
          onConfirm={onConfirmDeleteModal}
          onCancel={onCloseDeleteModal}
          message={t('company:complianceProgram.show.delete.confirm', { name: itemToDelete?.entity_name })}
        />
        <SnackbarAlert
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          message={snackbar.message}
          severity={snackbar.severity}
        />
      </Container>
    </>
  );
};
